import React, { FC, ReactNode, useState } from 'react';
import cn from 'classnames';

export type IconPropsType = {
  width?: number;
  height?: number;
  fill: string;
};

export const ButtonIcon = {
  default: {
    fill: '#80A2B1'
  },
  hover: {
    fill: '#FF4200'
  },
  disabled: {
    fill: '#D8D8D8'
  }
};

export const ButtonIconDarkBg = {
  default: {
    fill: '#FFFFFF'
  },
  hover: {
    fill: '#B2C7D0'
  },
  disabled: {
    fill: '#BDBDBD'
  }
};

export enum VariantBg {
  LIGHT = 'light',
  DARK = 'dark'
}

interface Props extends React.HTMLProps<HTMLButtonElement> {
  onClick?: (event: React.FormEvent<HTMLButtonElement>) => void;
  buttonLabel?: ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'submit' | 'reset' | 'button' | undefined;
  icon?: {
    size?: { width: number; height: number };
    alt: string;
    component: FC<IconPropsType>;
  };
  iconClass?: string;
  variantBg?: VariantBg;
  isBg?: boolean;
}

const IconButton: FC<Props> = ({
  onClick,
  className = '',
  disabled = false,
  icon,
  iconClass = '',
  variantBg = VariantBg.LIGHT,
  isBg = true,
  ...otherProps
}) => {
  const IconComponent = icon?.component;
  const ButtonRules =
    variantBg === VariantBg.LIGHT ? ButtonIcon : ButtonIconDarkBg;
  const [isHoverState, setStateHover] = useState(false);

  const generalProps = {
    className: cn(
      'flex items-center justify-center rounded-md',
      disabled && 'cursor-not-allowed',
      variantBg === VariantBg.LIGHT && isBg ? 'bg-gallery' : '',
      className
    ),
    onMouseEnter: () => setStateHover(true),
    onMouseLeave: () => setStateHover(false)
  };

  const WrapperComponent = ({ children }: { children: ReactNode }) => {
    if (!onClick) {
      // No onClick passed: render div instead of button
      return <div {...generalProps}>{children}</div>;
    }

    return (
      <button
        {...generalProps}
        {...otherProps}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    );
  };

  return (
    <WrapperComponent>
      <div
        className={cn(
          'flex items-center justify-center w-full h-full w-[40px] h-[40px]',
          iconClass
        )}
      >
        <IconComponent
          {...(isHoverState ? ButtonRules.hover : ButtonRules.default)}
          {...(disabled ? ButtonRules.disabled : {})}
          {...(icon?.size || {})}
        />
      </div>
    </WrapperComponent>
  );
};

export default IconButton;
